import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"

/** Never replace a user's unfinished work when inserting a starter or result. */
export function appendAgentDraft(existing: string, addition: string): string {
  if (!addition.trim()) return existing
  if (!existing.trim()) return addition
  return `${existing.trimEnd()}\n\n${addition}`
}

/** Existing thread visibility is authoritative. A missing row means unknown, not private. */
export function agentWorkspaceVisibility(
  agent: HostedAgentSummary,
  sessionId: string | null,
  session?: HostedAgentSession
): "open" | "personal" | "unknown" {
  if (!sessionId) return agent.memory_mode
  if (session?.visibility === "open" || session?.visibility === "personal")
    return session.visibility
  return "unknown"
}

/** Full routing prose is deliberately not a user-facing greeting. */
export function agentIntroduction(agent: HostedAgentSummary): string {
  return agent.profile.introduction?.trim() || agent.profile.title.trim()
}

/** Administrator-authored starters only; no assumed tool/role capabilities. */
export function agentStarterPrompts(agent: HostedAgentSummary): string[] {
  return [
    ...new Set((agent.profile.starter_prompts ?? []).map((value) => value.trim()).filter(Boolean)),
  ].slice(0, 3)
}

export function agentThreadDate(session: HostedAgentSession, locale: string): string {
  const timestamp = session.updated || session.created
  if (!timestamp) return ""
  const date = new Date(timestamp)
  if (!Number.isFinite(date.getTime())) return ""
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

/**
 * Elapsed time of the last turn, for the slim presence line ("Response
 * complete · 3.6 s"). Returns an empty string when the timing is incomplete,
 * so the line simply omits the segment instead of claiming a made-up number.
 * Formatting goes through `Intl` so Swedish reads "3,6 s", not "3.6 s".
 */
export function formatAgentDuration(
  milliseconds: number | null | undefined,
  locale: string
): string {
  if (milliseconds == null || !Number.isFinite(milliseconds) || milliseconds < 0) return ""
  if (milliseconds < 1000) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(milliseconds))} ms`
  }
  const seconds = milliseconds / 1000
  const digits = seconds < 100 ? 1 : 0
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(seconds)} s`
}

/**
 * Wall-clock duration of a finished turn from the explorer store's response
 * timing. `null` while a turn is running or when the timer never started.
 */
export function agentTurnDurationMs(
  timing: { requestStartMs: number; endMs: number | null } | null | undefined
): number | null {
  if (!timing || timing.endMs == null) return null
  const elapsed = timing.endMs - timing.requestStartMs
  return elapsed >= 0 ? elapsed : null
}

/**
 * Workspace layout of the agent chat surface (#3165).
 *
 * On a fresh thread the greeting, the starter cards and the composer are ONE
 * centred column, so the composer sits directly under the cards instead of
 * being docked with a hole above it. Once the thread has a turn, the composer
 * docks to the bottom as before.
 *
 * This is deliberately a class switch and not two render positions:
 * `Chat.Footer` keeps the unsent draft in its own local state, so moving it in
 * the tree would remount it and throw that draft away. What changes around it
 * is the column — `flex-initial` stops the message area from growing (and
 * stays shrinkable, so a short viewport still scrolls), and the local
 * `--chat-footer-height` override removes the bottom padding the docked
 * composer reserves, which is exactly the gap that would otherwise appear.
 * The footer's scroll-fade gradient is hidden too — with nothing scrolling
 * above the composer it would only haze the starter cards.
 */
export function agentWorkspaceLayoutClass(showEmptyState: boolean): string {
  const base = "relative flex h-full min-h-0 flex-1 flex-col"
  if (!showEmptyState) return base
  return `${base} justify-center [&>footer>div]:hidden [&>[data-testid=chat-content]]:flex-initial [&>[data-testid=chat-content]]:pt-0 [&>[data-testid=chat-content]]:[--chat-footer-height:0px]`
}

/**
 * The one answer column of the agent workspace (#3165): ~760 px, and a quiet
 * right-aligned user bubble — the shared bubble's filled `secondary` treatment
 * competes with the answer for attention, and its clipped corner belongs at the
 * bottom, next to the reply.
 *
 * The bubble override is anchored on `Chat.UserMessage`'s own `items-end` card
 * (`Card > section`): `Chat.SystemMessage` renders the very same shape, so a
 * bare `& > div > section` would paint every ANSWER as a card too.
 *
 * The answer prose is additionally capped at 66ch (mockup `Thread.dc.html`,
 * spec "Answer width capped at ~66ch per DESIGN.md"): the cap rides on the
 * shared answer body (`system-message-answer-text-<index>`) from HERE, inside
 * the agent column, so `Chat.SystemMessage` keeps its width everywhere else.
 * The action row is a sibling `CardFooter` (`system-message-actions`), so it
 * stays on the full 768 px column and the icons never wrap early.
 */
export const AGENT_ANSWER_COLUMN_CLASS =
  "focus-visible:ring-ring mx-auto flex w-full max-w-3xl flex-col gap-6 focus-visible:ring-2 [&>div.items-end>section]:bg-card [&>div.items-end>section]:text-card-foreground [&>div.items-end>section]:border-border [&>div.items-end>section]:rounded-xl [&>div.items-end>section]:rounded-tr-xl [&>div.items-end>section]:rounded-br-[4px] [&_[data-testid^=system-message-answer-text-]]:max-w-[66ch]"

/** "Sources · N" sits in the composer's 28 px options row, beside the "+". */
export const AGENT_COMPOSER_CONTROL_CLASS =
  "text-muted-foreground hover:text-foreground h-7 min-h-7 gap-1.5 px-2 text-xs font-medium"

/** Supported local parser formats, identical in cloud and disconnected on-prem. */
export const AGENT_CHAT_DROPZONE_OPTIONS = {
  accept: {
    "application/pdf": [".pdf"],
    "text/plain": [".txt"],
    "text/markdown": [".md"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  },
}

/** Image formats a user is likely to try as material; the local parser reads none of them. */
const IMAGE_FILE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
  ".tif",
  ".tiff",
  ".heic",
  ".heif",
  ".avif",
]

/** True for a file the browser types as an image, or that carries an image extension. */
export function isImageFile(file: Pick<File, "name" | "type">): boolean {
  if (file.type.toLowerCase().startsWith("image/")) return true
  const name = file.name.toLowerCase()
  return IMAGE_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))
}

/**
 * The toast that explains why the agent composer refused files (BUG-005).
 *
 * An image is the refusal users actually hit — a logo, a screenshot — and the
 * generic "this file cannot be read" reads like a broken file. Images get
 * their own message that says they are not supported as material YET and
 * lists what is; anything else keeps the generic unsupported-file text.
 */
export function agentSourceRejectionKey(files: ReadonlyArray<Pick<File, "name" | "type">>): string {
  return files.some(isImageFile)
    ? "agents.sources.images_unsupported"
    : "agents.sources.unsupported"
}
